import React from 'react';
import { IonContent, IonPage, useIonAlert, useIonRouter } from '@ionic/react';
import { RouteComponentProps } from 'react-router';
import { Header, IonLoading, Quantity, TabsOffset } from 'components';
import { analytics, axius, cartFx, env, func, images, types } from 'utils';
import { Alert, Button, ButtonGroup } from 'react-bootstrap';
import moment from 'moment';
import { _store, _update } from '_Store';
import _ from 'lodash';
import Paystack from '@paystack/inline-js';

import Cart2 from './Cart2';
import Checkout from './Checkout';
import ItemBottomModal from '../components/ItemBottmModal';

interface Props extends RouteComponentProps, types.StateProps {}

const Cart: React.FC<Props> = (props) => {
	const history = props.history;

	const [presentAlert] = useIonAlert();
	const router = useIonRouter();

	const {
		__auth: { user },
		__data: { cart, location, options, promotions, cartDefault },
	} = _store.useState();

	const [step, setStep] = React.useState(1);
	const [loading, setLoading] = React.useState('');
	const [isItemModalOpen, setOpenedItemModal] = React.useState(false);
	const [selectProduct, setSelectProduct] = React.useState<types.Item>();

	const cartTotal = cartFx.total(cart.items);
	const activeStore = cart.store;

	const deliveryDistance = +func.distanceMatrix(
		{ lat: location?.address?.x, lng: location?.address?.y },
		{
			lat: activeStore?.address?.x,
			lng: activeStore?.address?.y,
		},
		true
	);
	const minCartValue =
		location.option !== types.OrderTypes.DELIVERY ||
		cartTotal >= +(cart.store?.delivery?.cartMinimum || 0);

	React.useEffect(() => {
		analytics.trackEvent(analytics.eventTypes.VIEW_CART);

		activeStore?.id && getVenue();
		window.scroll(0, 0);

		const promoInterval = setInterval(() => {
			Promise.all([axius.get(`promotions`)]).then((res) => {
				res[0].status === 200 &&
					_update.data(
						'promotions',
						((res[0].data || []) as types.Promotion[]).filter(
							(promo) =>
								promo.isActive &&
								promo.providers.includes(types.OrderOrigins.REPDASH)
						)
					);
			});
		}, 30 * 1000);
		return () => {
			clearInterval(promoInterval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		return () => {
			if (!props.history.location.pathname.includes('/cart')) {
				setStep(1);
				setLoading('');
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.history.location.pathname]);

	React.useEffect(() => {
		if (history.location.pathname !== '/cart') {
			setStep(1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [history.location.pathname]);

	React.useEffect(() => {
		activeStore?.id && getVenue();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (cart.items.length > 0) {
			const cartCopy: types.Cart = func.copyObject(cart);
			if (!cartCopy.promotions) {
				cartCopy.promotions = [];
			}
			const cartItems = cartCopy.items;
			for (let i = 0; i < cartItems.length; i++) {
				const item = cartItems[i];
				const hasPromos = cartFx.myPromos({ promotions, item, user });
				let discount = 0;
				for (const hasPromo of hasPromos) {
					const promoAppliesToItem = hasPromo?.items?.includes(item.code);
					if (promoAppliesToItem && !cartCopy.voucher) {
						const isOrderMinimum =
							hasPromo && +hasPromo.limit.minimumOrder <= cartTotal;
						if (hasPromo && isOrderMinimum) {
							discount = cartFx.promoDiscount(
								hasPromo,
								item.priceTotal / item.quantity
							);
							if (discount > 0) {
								cartItems[i]['promotion'] = hasPromo.id;
							}
						} else {
							delete cartItems[i]['promotion'];
						}
						cartItems[i]['discount'] = discount * item.quantity;
						cartItems[i]['priceTotal'] = item.priceTotal;
						cartItems[i]['description'] = item.description;
					}
				}
			}
			let discount = 0;
			const hasPromosALL =
				cartFx
					.myPromos({ promotions, user })
					.filter((promo) => promo.type.includes('ALL')) || [];
			for (const hasPromo of hasPromosALL) {
				if (hasPromo && hasPromo.type.includes('ALL') && !cartCopy.voucher) {
					discount += cartFx.promoDiscount(hasPromo, 0);
					cartCopy.promotions.push(hasPromo.id);
				}
			}

			if (!cartCopy.voucher) {
				cartCopy.promotions = _.uniq(cartCopy.promotions);
				_update.data('cart', { ...cartCopy, discount, items: cartItems });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cart.items, promotions, step]);

	const getVenue = (onOk: any = null) => {
		axius.get(`stores/${activeStore?.code}/items`).then((res) => {
			if (onOk) {
				if (res.store && res.data) {
					onOk(res.data, res.store);
				} else {
					setLoading(``);
					presentAlert(
						`Sorry! We could not initiate a payment, please try again.`,
						[
							{
								text: 'Okay',
								handler: () => {
									func.clearCache('./cart');
								},
							},
						]
					);
				}
			} else {
				if (res.store) {
					const kart = func.getStorageJson('cart');
					_update.data('cart', { ...kart, store: res.store });
				}
			}
		});
	};

	const updateCart = (item: types.ItemCart, increment: '+' | '-') => {
		const copyCart = func.copyObject(cart) as types.Cart;
		const doUpdate = () => {
			_update.data('cart', {
				...copyCart,
				items: cartFx.add(item, copyCart.items, increment),
			});
		};
		if (item.quantity === 1 && increment === '-') {
			presentAlert({
				header: `Remove`,
				message: `Do you want to remove this product from your cart?`,
				buttons: [
					{ text: `Cancel` },
					{
						text: `Remove`,
						handler: () => {
							if (cart.items?.length === 1) {
								analytics.trackEvent(analytics.eventTypes.ORDER_CANCEL);
								_update.data('cart', cartDefault);
							} else {
								_update.data('cart', {
									...copyCart,
									items: cartFx.remove(item, copyCart.items),
									store: copyCart.items.length === 1 ? {} : activeStore,
								});
							}
						},
					},
				],
			});
		} else {
			doUpdate();
		}
	};

	const findVariants = (codes: string[]) => {
		return cart.items
			.map((item) => {
				return Object.keys(item.variants)
					.map((v) => {
						return (item.variants as any)[v].map((iv: any) => {
							return iv;
						});
					})
					.flat();
			})
			.flat()
			.filter((as) => codes.includes(as.code))
			.map((as) => as.name)
			.join(', ');
	};

	const checkout = (deliveryFee: number) => {
		if (
			(location.option === types.OrderTypes.DELIVERY && minCartValue) ||
			location.option === types.OrderTypes.PICKUP
		) {
			if (!location.address.houseNo || !location.address.street) {
				presentAlert(`Your house number and street name are required`);
				return;
			}
			analytics.trackEvent(analytics.eventTypes.BEGIN_CHECKOUT, {
				value: cartTotal,
				delivery_fee: deliveryFee,
				currency: 'NGN',
				items: cart.items.map((item) => {
					return {
						id: item.id,
						item_name: item.name,
						price: item.priceTotal,
						quantity: item.quantity,
					};
				}),
			});
			func.scrollToTop();
			if (step === 3) {
				if (user.id) {
					if (func.checkKYC(user).email && func.checkKYC(user).phone) {
						if (
							cart.paymentType &&
							(location.option === 'PICKUP' ? true : cart.delivery.channel)
						) {
							setLoading(`validating order...`);
							getVenue((items: types.Item[], store: types.Store) => {
								_update.data('activeStore', store);
								if (
									store?.preferences?.canTakeOrders &&
									store?.preferences?.online
								) {
									const discount =
										cart.discount +
										cart.items.reduce((a, b) => a + (b.discount || 0), 0);
									const {
										cantDeliver,
										notAvailable,
										notPublished,
										sidesUnavailables,
									} = itemsAvailability(items, store);
									if (
										(cantDeliver.length === 0 ||
											location.option === types.OrderTypes.PICKUP) &&
										notPublished.length === 0 &&
										notAvailable.length === 0
									) {
										if (sidesUnavailables.length === 0) {
											const orderData = {
												type: location.option,
												items: cart.items.map((item) => {
													const { cartItemId, ...rest } = item;
													return rest;
												}),
												storeCode: activeStore?.code,
												address: location.address,
												paymentType: cart.paymentType,
												delivery: cart.delivery,
												amount: {
													total: cartTotal + deliveryFee - discount,
													amount: cartTotal,
													discount,
													delivery: deliveryFee,
												},
												promotions: cart.promotions,
												voucherCode: cart.voucher,
												paymentToken: '',
												source: func.config.platform,
											};
											switch (cart.paymentType) {
												default:
													createOrder(orderData, (order) => {
														const paystack = new Paystack();
														paystack.checkout({
															key: String(env.PAYSTACK_PUB),
															email: user.email,
															amount: order.amount.total * 100,
															currency: 'NGN',
															lastName: user.lastName,
															firstName: user.firstName,
															reference: order.uuid,
															metadata: {
																custom_fields: [
																	{
																		value: func.venueName(activeStore),
																		variable_name: 'storeCode',
																		display_name: 'Store',
																	},
																],
															},
															onLoad: () => {
																setTimeout(() => {
																	setLoading('');
																}, 3_000);
															},
															onSuccess: (paystackResponse) => {
																if (paystackResponse.status === 'success') {
																	setLoading(`...`);
																	_update.data('cart', cartDefault);
																	analytics.trackEvent(
																		analytics.eventTypes.PURCHASE_COMPLETE,
																		{
																			value: cartTotal,
																			currency: 'NGN',
																			transaction_id: order.uuid.split('_')[1],
																			items: cart.items.map((item) => {
																				return {
																					id: item.id,
																					item_name: item.name,
																					price: item.priceTotal,
																					quantity: item.quantity,
																				};
																			}),
																		}
																	);
																	setTimeout(() => {
																		func.redirect(
																			`/webhook/${order.uuid.split('_')[1]}`
																		);
																	}, 1_000);
																}
															},
															onError: (err) => {
																console.log('Payment failed:', err);
															},
														});
													});
													break;

												case types.OrderPaymentTypes.WALLET:
													createOrder(orderData, () => {});
													break;
											}
										} else {
											setLoading('');
											presentAlert(
												`We're sorry. We run out of the following item(s): ${findVariants(sidesUnavailables)}`
											);
										}
									} else {
										setLoading('');
										presentAlert(`
											We're sorry. The following item(s) are not available for 
											${location.option}: ${[...cantDeliver, ...notPublished, ...notAvailable]
												.map((cd) => {
													return cd.name;
												})
												.join(', ')}
										`);
									}
								} else {
									setLoading('');
									presentAlert(
										`We're sorry. We are not taking any orders at this moment`
									);
								}
							});
						} else {
							let errorMessage = '';
							if (!cart.paymentType) {
								errorMessage += 'Please choose a payment option';
							}
							if (!cart.delivery.channel) {
								errorMessage += '<br/>Please choose a delivery partner';
							}
							presentAlert(errorMessage);
						}
					} else {
						emailPrompt();
					}
				} else {
					authPrompt();
				}
			} else {
				continueCarting();
			}
		} else {
			presentAlert(
				`We require a minimum order of <b>₦${func.numberFormat(
					activeStore?.delivery.cartMinimum,
					2
				)}</b> to qualify for delivery.`
			);
		}
	};

	const createOrder = (orderData: any, onOK: (data: any) => void) => {
		setLoading('placing order...');
		axius.post('orders', orderData).then(async (res: types.AxiusResponse) => {
			setLoading('');
			if (res.status === 201) {
				analytics.trackEvent(analytics.eventTypes.ORDER_PLACED);
				_update.data('cache', {
					user: moment().unix(),
				});
				onOK(res.data);
			} else {
				setLoading('');
				presentAlert(res.message || 'Error');
			}
		});
	};

	const authPrompt = () => {
		presentAlert({
			header: `Log in or sign up!`,
			message: `Login in to complete your order`,
			buttons: [
				{ text: `Cancel` },
				{
					text: `Continue`,
					handler: () => {
						router.push('/login');
					},
				},
			],
		});
	};

	const emailPrompt = () => {
		presentAlert({
			header: `Please complete your account registration in your profile`,
			message: `Please add and verify your email address to complete checkout`,
			buttons: [
				{ text: `Cancel` },
				{
					text: `Set my email`,
					handler: () => {
						func.setStorageJson('edit_user', {
							from: props.location.pathname,
							edit: ['email'],
							option: 'promos',
						});
						router.push('/user');
					},
				},
			],
		});
	};

	const continueCarting = () => {
		if (location.option === types.OrderTypes.DELIVERY) {
			setLoading('calculating delivery fee...');
			axius
				.post('orders/delivery-fee', {
					city: func.myCity(),
					storeCode: activeStore.code,
					amount: cartTotal,
					source: {
						x: activeStore?.address?.x,
						y: activeStore?.address?.y,
					},
					destination: {
						x: location.address.x,
						y: location.address.y,
					},
				})
				.then((res) => {
					if (res.data && res.data.length > 0) {
						_update.data('deliveryFees', res.data);
						if (res.data.length === 1) {
							_update.data('cart', {
								...cart,
								delivery: { ...cart.delivery, channel: res.data[0].partner },
								deliveryFee: res.data[0].amount,
								paymentType: '',
								promotions: [],
							});
						} else {
							_update.data('cart', {
								...cart,
								delivery: { ...cart.delivery, channel: '' },
								deliveryFee: 0,
								paymentType: '',
								promotions: [],
							});
						}
						setStep(3);
					} else {
						// presentAlert(`We do not deliver to ${location.address.city}. Please change your location.`);
						presentAlert(
							`Oops! Our riders are all on the run right now 🏃🏽💨. Sorry for the wait—please try again in a few minutes!`
						);
					}
					setLoading('');
				});
		} else {
			_update.data('deliveryFees', []);
			_update.data('cart', {
				...cart,
				delivery: { ...cart.delivery, channel: '' },
				deliveryFee: 0,
				paymentType: '',
				promotions: [],
			});
			setStep(3);
			// setStep((s) => s + 1);
		}
	};

	const itemsAvailability = (items: any[], store: types.Store) => {
		const refreshedItems =
			items.filter((men) =>
				cart.items.map((ci: types.ItemCart) => ci.id).includes(men.id)
			) || [];
		const cantDeliver =
			refreshedItems.filter(
				(ri) => !(ri.categories[0] || {}).canDeliver || !ri.canDeliver
			) || [];
		const notPublished =
			refreshedItems.filter((ri) => ri.status === types.ItemStatuses.DRAFT) ||
			[];
		const notAvailable =
			refreshedItems.filter(
				(ri) =>
					!(
						store.preferences.itemsAvailable ||
						refreshedItems.map((ri) => ri.code)
					).includes(ri.code)
			) || [];

		const allSides = cart.items
			.map((item: types.ItemCart) => {
				return Object.keys(item.variants)
					.map((v) => {
						return (item.variants as any)[v]
							.map((iv: any) => {
								return iv.code;
							})
							.flat();
					})
					.flat();
			})
			.flat()
			.filter((as: string) => as);
		const availableSides = store.preferences?.sidesAvailable || [];
		const sidesUnavailables =
			availableSides.length > 0
				? allSides.filter((as: string) => !availableSides.includes(as))
				: [];

		return {
			cantDeliver,
			notPublished,
			notAvailable,
			refreshedItems,
			sidesUnavailables,
		};
	};

	const goToItem = async (item: types.Item, itemLink: string) => {
		// _update.data('item', item);
		// router.push(itemLink);
		setSelectProduct(item);
		setOpenedItemModal(true);
		_update.data('is_bottom_modal', { is_open: true });
	};

	const isOpen = func.isOpen(activeStore);

	return (
		<IonPage id="Cart">
			<Header
				{...props}
				showSearch={false}
				showSegments={false}
				showBack={step > 1}
				goBack={() => {
					if (step === 1) {
						props.history.goBack();
					} else {
						setStep(1);
						// step === 3 && location.option === types.OrderTypes.DELIVERY && Object.keys(deliveryPartners).length === 1
						// if (location.option === types.OrderTypes.DELIVERY) {
						//   setStep(1);
						// } else {
						//   setStep((s) => s - 1);
						// }
					}
				}}
			/>
			<IonContent fullscreen={false}>
				<IonLoading
					open={loading ? true : false}
					message={loading}
					onCancel={() => setLoading('')}
				/>
				{step === 1 && (
					<div id="Cart" className="content c-small">
						{cart.items.length > 0 && (
							<div>
								<div>&nbsp;</div>
								<div
									id="Segments"
									className="mb-3 d-flex justify-content-center"
								>
									<ButtonGroup>
										{options.map((seg, i) => (
											<Button
												key={`${i}_${seg.key}`}
												variant={location.option === seg.key ? 'active' : ''}
												onClick={() => {
													if (
														!activeStore.delivery.options.includes(
															seg.key.toUpperCase()
														)
													) {
														presentAlert(
															`This store does not offer ${seg.key}`
														);
													} else {
														_update.data('location', { option: seg.key });
													}
												}}
											>
												{seg.label}
											</Button>
										))}
									</ButtonGroup>
								</div>
							</div>
						)}
						<div className="divider hide" />

						{cart.items.length > 0 && (
							<>
								<div className="px-4">
									<div className="m-0 fw-500s text-muted text-uppercases hide">
										{func.venueName(cart.store)}
									</div>
									{!minCartValue && (
										<div className="text-center mt-3">
											<Alert variant="danger">
												We require a minimum order of{' '}
												<b>
													₦
													{func.numberFormat(
														cart.store?.delivery.cartMinimum,
														2
													)}
												</b>{' '}
												to qualify for delivery.
												<div>
													<div
														className="pointer ins fw-500 mt-2"
														onClick={() =>
															_update.data('location', { option: 'pickup' })
														}
													>
														Change to pickup
													</div>
												</div>
											</Alert>
										</div>
									)}
								</div>
								<div className="divider hide" />
							</>
						)}

						<div className="p-3">
							{cart.items.length === 0 && (
								<div
									id="Loading"
									className="d-flex justify-content-center align-items-center"
									style={{ height: '63vh' }}
								>
									<div className="text-center">
										<img src={images.CartEmpty} alt="CartEmpty" height="90" />
										<div className="mt-2" style={{ fontSize: 20 }}>
											Your cart is empty!
										</div>
									</div>
								</div>
							)}

							{cart.items.length > 0 && (
								<div>
									{(cart.items || []).map((item, i) => {
										const discount = item.discount;
										const priceTotal = item.priceTotal;
										const itemLink = `/i/${func.seourl(item.name)}.${activeStore.code}.${item.code}.edit.${item.cartItemId}`;
										const hasPromo = cartFx.myPromos({
											promotions,
											item,
											user,
										})[0];
										const isOrderMinimum =
											hasPromo && +hasPromo.limit.minimumOrder <= cartTotal;
										return (
											<div key={`${i}_${item.cartItemId}`}>
												<div className="d-flex">
													<div className="" style={{ width: '5rem' }}>
														<div
															className="bg-img contain border"
															style={{
																height: '4rem',
																borderRadius: 4,
																backgroundImage: `url("${(item.imagesLinks || [])[0] || images.Logo}")`,
															}}
														>
															{hasPromo && (
																<div className="item-discount m-1">
																	<i
																		className={`fa fa-gift primary fs-12 ${isOrderMinimum ? 'mr-1s' : ''}`}
																	/>
																</div>
															)}
														</div>
													</div>
													<div
														className="pl-3"
														style={{ width: 'calc(100vw - 5rem)' }}
													>
														<div className="fw-500 text-truncate">
															{item.name}
														</div>
														{discount > 0 ? (
															<div className="d-flex align-items-center">
																<div className="smalls mb-2 mr-2">
																	₦{func.numberFormat(priceTotal - discount)}
																</div>
																<del className="smalls mb-2 text-muted">
																	₦{func.numberFormat(priceTotal)}
																</del>
															</div>
														) : (
															<div className="smalls mb-2">
																₦{func.numberFormat(priceTotal)}
															</div>
														)}
														{Object.keys(item.variants).map((op, i) => (
															<div key={`${i}_${op}`}>
																{item.variants[op].map((po, j) => (
																	<div
																		key={`${j}_${po.name}`}
																		className="small text-muted"
																	>
																		{po.quantity > 1 ? `${po.quantity} x ` : ''}{' '}
																		{po.name}{' '}
																		{po.price > 0
																			? `(+₦${func.numberFormat(+po.price * +po.quantity)})`
																			: ''}
																	</div>
																))}
															</div>
														))}
														<div className="row align-items-center mt-3">
															<div className="col-6">
																{Object.keys(item.variants).length > 0 && (
																	<div
																		className="small text-muted"
																		onClick={() =>
																			goToItem(
																				item as unknown as types.Item,
																				itemLink
																			)
																		}
																	>
																		Edit Selections
																	</div>
																)}
															</div>
															<div className="col-6">
																<Quantity
																	value={item.quantity || 1}
																	increase={() => updateCart(item, '+')}
																	decrease={() => updateCart(item, '-')}
																/>
															</div>
														</div>
													</div>
												</div>
												<div className="my-3 divider" />
											</div>
										);
									})}

									<div className="footers pb-3 mt-5">
										<Button
											variant={`${minCartValue ? 'secondary' : 'gray'} btn-block btn-lg text-uppercases`}
											onClick={() => checkout(deliveryDistance)}
											disabled={
												!activeStore.delivery.options.includes(
													location.option
												) || !isOpen
											}
										>
											<div className="d-flex justify-content-between">
												<div>Checkout</div>
												<div>
													<b>
														₦
														{func.numberFormat(
															cartFx.total(cart.items, true, true),
															2
														)}
													</b>
												</div>
											</div>
										</Button>
									</div>

									<div className="text-center my-2">
										<span
											className="primary fw-600 ins"
											onClick={() => router.push(`/store/${activeStore.code}`)}
										>
											Continue Shopping
										</span>
									</div>
								</div>
							)}
						</div>
					</div>
				)}
				{step === 2 && (
					<Cart2 {...props} checkout={checkout} minCartValue={minCartValue} />
				)}
				{step === 3 && (
					<Checkout
						{...props}
						checkout={checkout}
						minCartValue={minCartValue}
					/>
				)}
				<TabsOffset />
				{selectProduct && (
					<ItemBottomModal
						isItemModalOpen={isItemModalOpen}
						setOpenedItemModal={setOpenedItemModal}
						selectedProduct={selectProduct}
					/>
				)}{' '}
			</IonContent>
		</IonPage>
	);
};

export default Cart;
